import { useRef } from 'react';
import { Box, Container, Grid, Typography, Stack, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import PageHero from '../components/common/PageHero';
import CTABanner from '../components/common/CTABanner';

const navy  = '#0F1D2E';
const navyL = '#1B2D42';
const gold  = '#D1BC8A';
const goldD = '#B8A06E';

// Icon resolver: supports React components (static data) and string names (API data)
function resolveIcon(icon) {
  if (typeof icon === 'function' || (typeof icon === 'object' && icon?.$$typeof)) return icon;
  if (typeof icon === 'string') {
    try { return require('../admin/utils/iconMap').getIcon(icon); } catch { return null; }
  }
  return null;
}

function InView({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px 0px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 22 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default function ServicePage({ data }) {
  if (!data) return null;

  const Icon = data.icon || resolveIcon(data.icon_name);
  const features = data.features || [];
  const process  = data.process  || [];

  return (
    <>
      <PageHero overline={data.subtitle} title={data.title} />

      {/* ── Intro + features ─────────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 7, md: 10 }, bgcolor: '#F8F7F5' }}>
        <Container maxWidth="lg">

          {/* Description + CTAs */}
          <InView>
            <Grid container spacing={{ xs: 3, md: 8 }} alignItems="flex-start" sx={{ mb: { xs: 6, md: 8 } }}>
              <Grid size={{ xs: 12, md: 7 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  {Icon && (
                    <Box sx={{
                      width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
                      bgcolor: 'rgba(209,188,138,0.14)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: gold,
                    }}>
                      <Icon sx={{ fontSize: 24 }} />
                    </Box>
                  )}
                  <Typography sx={{ color: gold, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.2em' }}>
                    {data.subtitle || data.title}
                  </Typography>
                </Stack>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85, fontSize: '1rem' }}>
                  {data.description || data.heroDescription}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Stack spacing={1.5}>
                  <Button
                    variant="contained" fullWidth size="large"
                    component={RouterLink} to="/anfrage" endIcon={<ArrowForwardIcon />}
                    sx={{
                      background: `linear-gradient(135deg, ${gold} 0%, ${goldD} 100%)`,
                      color: navy, fontWeight: 800, borderRadius: '12px', py: 1.4,
                      boxShadow: `0 6px 20px rgba(209,188,138,0.28)`,
                      '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 12px 32px rgba(209,188,138,0.38)` },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Anfrage senden
                  </Button>
                  <Button
                    variant="outlined" fullWidth size="large"
                    component={RouterLink} to="/referenzen"
                    sx={{
                      borderColor: 'rgba(15,29,46,0.18)', color: navy, fontWeight: 600,
                      borderRadius: '12px', py: 1.4,
                      '&:hover': { borderColor: navy, bgcolor: navy, color: '#fff' },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Referenzen ansehen
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </InView>

          {/* Feature cards */}
          {features.length > 0 && (
            <>
              <InView>
                <Typography sx={{ color: gold, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.2em', mb: 0.75 }}>
                  Leistungen im Detail
                </Typography>
                <Typography variant="h4" sx={{ color: navy, fontWeight: 800, mb: 5, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                  Was wir für Sie leisten
                </Typography>
              </InView>
              <Grid container spacing={2}>
                {features.map((feature, i) => {
                  const FIcon = resolveIcon(feature.icon || feature.icon_name);
                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                      <InView delay={i * 0.07}>
                        <Box sx={{
                          p: 2.5, bgcolor: '#fff', borderRadius: '16px',
                          border: '1px solid rgba(15,29,46,0.07)',
                          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                          height: '100%',
                          transition: 'all 0.25s ease',
                          '&:hover': { borderColor: 'rgba(209,188,138,0.4)', boxShadow: '0 8px 28px rgba(0,0,0,0.09)', transform: 'translateY(-3px)' },
                        }}>
                          <Box sx={{
                            width: 46, height: 46, borderRadius: '12px', mb: 2,
                            bgcolor: 'rgba(209,188,138,0.12)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: gold,
                          }}>
                            {FIcon && <FIcon sx={{ fontSize: 24 }} />}
                          </Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: navy, mb: 0.75, lineHeight: 1.3 }}>
                            {feature.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                            {feature.description}
                          </Typography>
                        </Box>
                      </InView>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}
        </Container>
      </Box>

      {/* ── Process steps ────────────────────────────────────────────────────── */}
      {process.length > 0 && (
        <Box sx={{ py: { xs: 7, md: 10 }, bgcolor: '#fff' }}>
          <Container maxWidth="lg">
            <InView>
              <Typography sx={{ color: gold, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.2em', mb: 0.75 }}>
                Ablauf
              </Typography>
              <Typography variant="h4" sx={{ color: navy, fontWeight: 800, mb: 6, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                So arbeiten wir
              </Typography>
            </InView>

            <Grid container spacing={2.5}>
              {process.map((step, i) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                  <InView delay={i * 0.08}>
                    <Box sx={{
                      p: 2.5, bgcolor: '#F8F7F5', borderRadius: '16px',
                      border: '1px solid rgba(15,29,46,0.06)',
                      height: '100%', position: 'relative',
                    }}>
                      {/* Step number */}
                      <Typography sx={{
                        position: 'absolute', top: 16, right: 20,
                        color: 'rgba(15,29,46,0.05)', fontWeight: 900, fontSize: '3.5rem',
                        lineHeight: 1, userSelect: 'none',
                      }}>
                        {String(i + 1).padStart(2, '0')}
                      </Typography>

                      <Box sx={{
                        width: 36, height: 36, borderRadius: '50%', mb: 2,
                        background: `linear-gradient(135deg, ${gold} 0%, ${goldD} 100%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <CheckIcon sx={{ fontSize: 18, color: navy }} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: navy, mb: 0.75 }}>
                        {step.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, fontSize: '0.85rem' }}>
                        {step.description}
                      </Typography>
                    </Box>
                  </InView>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      <CTABanner pageKey="home" />
    </>
  );
}
